/**
 * Isometric "product screens" for the module pages.
 *
 * The original Webflow site showed a localised UI screenshot in each feature
 * hero. Instead of shipping raster screenshots we describe the screen as data
 * and draw it in the site's isometric style — so every label goes through the
 * normal translation pipeline and the artwork follows the active theme.
 */

export type ScreenTone = "ok" | "warn" | "late" | "muted";

export type ScreenRow = {
  /** Cell values, aligned with `columns`. Last cell may be omitted when a chip is used. */
  cells: string[];
  /** Status chip rendered in the trailing column. */
  chip?: { label: string; tone: ScreenTone };
  /** Progress bar 0..100 rendered under the first cell. */
  progress?: number;
};

export type ScreenLane = {
  lane: string;
  bars: { start: number; span: number; label: string; tone?: ScreenTone }[];
};

export type ScreenDef = {
  /** Window title bar. */
  title: string;
  /** Tab strip; the first one renders as active. */
  tabs: string[];
  /** Toolbar action (primary button). */
  action: string;
  columns: string[];
  rows: ScreenRow[];
  /** Optional Gantt-style lanes drawn under the table. */
  lanes?: ScreenLane[];
  /** Optional side panel with label/value pairs. */
  side?: { title: string; lines: { label: string; value: string }[] };
  alt: string;
  caption: string;
};

export const FEATURE_SCREENS: Record<string, ScreenDef> = {
  sales: {
    title: "Sales orders",
    tabs: ["Open", "Confirmed", "Shipped"],
    action: "New order",
    columns: ["Order", "Customer", "Due date", "Status"],
    rows: [
      { cells: ["SO-2041", "Nordkabel AB", "12 Mar"], chip: { label: "Confirmed", tone: "ok" } },
      { cells: ["SO-2042", "Metalika LLC", "14 Mar"], chip: { label: "Reserved", tone: "muted" } },
      { cells: ["SO-2043", "Delta Foods", "15 Mar"], chip: { label: "Partly shipped", tone: "warn" } },
      { cells: ["SO-2044", "Alu Systems", "18 Mar"], chip: { label: "Awaiting stock", tone: "late" } },
    ],
    side: {
      title: "Order SO-2042",
      lines: [
        { label: "Lines", value: "7" },
        { label: "Reserved from stock", value: "5 / 7" },
        { label: "Order value", value: "18 420 €" },
        { label: "Promised date", value: "14 Mar" },
      ],
    },
    alt: "Isometric mock-up of the sales order list with statuses and an order detail panel",
    caption: "Sales orders with live stock reservation",
  },

  production: {
    title: "Manufacturing orders",
    tabs: ["In progress", "Released", "Finished"],
    action: "Release order",
    columns: ["Order", "Item", "Work centre", "Progress"],
    rows: [
      { cells: ["MO-881", "Cable 3x2.5", "Extrusion 1"], progress: 82 },
      { cells: ["MO-882", "Drum assembly", "Winding 2"], progress: 47 },
      { cells: ["MO-883", "Housing 120", "CNC 3"], progress: 24 },
      { cells: ["MO-884", "Harness kit", "Assembly"], progress: 8 },
    ],
    side: {
      title: "Order MO-882",
      lines: [
        { label: "Planned qty", value: "1 200 m" },
        { label: "Reported", value: "560 m" },
        { label: "Scrap", value: "1.8 %" },
        { label: "Operations done", value: "3 / 6" },
      ],
    },
    alt: "Isometric mock-up of the production order list with operation progress bars",
    caption: "Shop-floor progress reported per operation",
  },

  purchases: {
    title: "Purchasing",
    tabs: ["Requests", "Purchase orders", "Receipts"],
    action: "Create PO",
    columns: ["Document", "Supplier", "Expected", "Status"],
    rows: [
      { cells: ["PR-318", "Internal request", "—"], chip: { label: "To approve", tone: "muted" } },
      { cells: ["PO-1042", "CopperTrade", "11 Mar"], chip: { label: "Confirmed", tone: "ok" } },
      { cells: ["PO-1043", "PolyMat GmbH", "13 Mar"], chip: { label: "Partly received", tone: "warn" } },
      { cells: ["PO-1044", "Drum Supply", "09 Mar"], chip: { label: "Overdue", tone: "late" } },
    ],
    side: {
      title: "Order PO-1043",
      lines: [
        { label: "Ordered", value: "4 500 kg" },
        { label: "Received (GRN)", value: "3 100 kg" },
        { label: "Linked demand", value: "MO-881" },
        { label: "Order value", value: "27 900 €" },
      ],
    },
    alt: "Isometric mock-up of the purchasing screen with requests, purchase orders and receipt statuses",
    caption: "From purchase request to goods receipt",
  },

  stocks: {
    title: "Stock on hand",
    tabs: ["By item", "By location", "Batches"],
    action: "Stock move",
    columns: ["Item", "Location", "On hand", "Status"],
    rows: [
      { cells: ["Copper wire 2.5", "WH-1 / A-04", "6 120 kg"], chip: { label: "Available", tone: "ok" } },
      { cells: ["PE compound", "WH-1 / B-11", "980 kg"], chip: { label: "Reserved", tone: "muted" } },
      { cells: ["Drum 1600", "Yard", "42 pcs"], chip: { label: "Below min", tone: "warn" } },
      { cells: ["Pigment lime", "WH-2 / C-02", "0 kg"], chip: { label: "Out of stock", tone: "late" } },
    ],
    side: {
      title: "Batch LOT-4471",
      lines: [
        { label: "Quantity", value: "1 240 kg" },
        { label: "Received", value: "02 Mar" },
        { label: "Reserved for", value: "MO-881" },
        { label: "Traceability", value: "Full" },
      ],
    },
    alt: "Isometric mock-up of the stock screen with locations, batches and availability chips",
    caption: "Stock, batches and reservations per location",
  },

  planning: {
    title: "Material planning",
    tabs: ["Demand", "Supply", "Shortages"],
    action: "Run planning",
    columns: ["Item", "Demand", "Coverage", "Action"],
    rows: [
      { cells: ["Copper wire 2.5", "8 400 kg", "6 120 kg"], chip: { label: "Order 2 280 kg", tone: "warn" } },
      { cells: ["PE compound", "1 500 kg", "1 800 kg"], chip: { label: "Covered", tone: "ok" } },
      { cells: ["Drum 1600", "60 pcs", "42 pcs"], chip: { label: "Order 18 pcs", tone: "warn" } },
      { cells: ["Pigment lime", "120 kg", "0 kg"], chip: { label: "Critical", tone: "late" } },
    ],
    side: {
      title: "Planning run",
      lines: [
        { label: "Horizon", value: "8 weeks" },
        { label: "Suggested POs", value: "12" },
        { label: "Suggested MOs", value: "7" },
        { label: "Shortages left", value: "1" },
      ],
    },
    alt: "Isometric mock-up of the planning screen showing demand, coverage and suggested orders",
    caption: "Demand, coverage and what to order next",
  },

  scheduling: {
    title: "Production schedule",
    tabs: ["Work centres", "Week", "Load"],
    action: "Reschedule",
    columns: ["Work centre", "Load", "Queue", "Status"],
    rows: [
      { cells: ["Extrusion 1", "92 %", "4 ops"], chip: { label: "At capacity", tone: "warn" } },
      { cells: ["Winding 2", "64 %", "3 ops"], chip: { label: "On plan", tone: "ok" } },
    ],
    lanes: [
      {
        lane: "Extrusion 1",
        bars: [
          { start: 2, span: 26, label: "MO-881", tone: "ok" },
          { start: 34, span: 30, label: "MO-884", tone: "warn" },
        ],
      },
      {
        lane: "Winding 2",
        bars: [
          { start: 8, span: 22, label: "MO-882", tone: "ok" },
          { start: 44, span: 26, label: "MO-885", tone: "muted" },
        ],
      },
      {
        lane: "Assembly",
        bars: [
          { start: 18, span: 18, label: "MO-883", tone: "muted" },
          { start: 56, span: 24, label: "MO-886", tone: "late" },
        ],
      },
    ],
    alt: "Isometric mock-up of the scheduling board with work centre lanes and operation bars",
    caption: "Finite schedule across work centres",
  },

  cost: {
    title: "Production cost",
    tabs: ["Per order", "Per item", "Variance"],
    action: "Recalculate",
    columns: ["Cost element", "Planned", "Actual", "Variance"],
    rows: [
      { cells: ["Materials", "12 400 €", "12 980 €", "+4.7 %"] },
      { cells: ["Labour", "3 100 €", "2 940 €", "−5.2 %"] },
      { cells: ["Machine time", "1 850 €", "2 010 €", "+8.6 %"] },
      { cells: ["Overheads", "1 200 €", "1 200 €", "0 %"] },
    ],
    side: {
      title: "Order MO-881",
      lines: [
        { label: "Output", value: "1 200 m" },
        { label: "Cost per metre", value: "15.94 €" },
        { label: "Quoted price", value: "19.50 €" },
        { label: "Margin", value: "18.3 %" },
      ],
    },
    alt: "Isometric mock-up of the costing screen comparing planned and actual cost elements",
    caption: "Planned vs actual cost, rolled up live",
  },

  "technical-data": {
    title: "Item master data",
    tabs: ["Items", "BOM", "Routing"],
    action: "New revision",
    columns: ["Level", "Component", "Qty", "Revision"],
    rows: [
      { cells: ["1", "Cable 3x2.5", "1 m", "Rev. C"], chip: { label: "Active", tone: "ok" } },
      { cells: ["1.1", "Copper wire 2.5", "3 x 24 g", "Rev. B"] },
      { cells: ["1.2", "PE insulation", "42 g", "Rev. C"] },
      { cells: ["1.3", "Outer sheath", "58 g", "Rev. A"], chip: { label: "In change (ECN)", tone: "warn" } },
    ],
    side: {
      title: "Routing 3x2.5",
      lines: [
        { label: "Operations", value: "6" },
        { label: "Setup time", value: "45 min" },
        { label: "Run time", value: "0.9 min / m" },
        { label: "Approved", value: "Rev. C" },
      ],
    },
    alt: "Isometric mock-up of the master data screen with a BOM tree and routing revisions",
    caption: "BOM levels and routing revisions in sync",
  },
};
